"use client";

import { useState } from "react";
import dynamic from "next/dynamic";
import HeroSection from "@/components/client/landing/sections/HeroSection";
import HowItWorks from "@/components/client/landing/sections/HowitWorks";
import { FeaturedPack } from "@/components/client/landing/sections/FeaturedPack";

// Lazy loaded sections
const SpecialOffers = dynamic(() => import("@/components/client/landing/sections/SpecialOffers").then(mod => mod.SpecialOffers), {
    ssr: false,
    loading: () => <div className="h-96 w-full animate-pulse bg-gray-50 rounded-3xl" />
});
const ProductGridSection = dynamic(() => import("@/components/client/landing/sections/ProductGridSection").then(mod => mod.ProductGridSection), {
    ssr: false,
    loading: () => <div className="h-96 w-full animate-pulse bg-gray-50 rounded-3xl" />
});
const AlgeriaSection = dynamic(() => import("@/components/client/landing/sections/AlgeriaSection").then(mod => mod.AlgeriaSection), {
    ssr: false
});
const QuickViewModal = dynamic(() => import("@/components/shared/model/QuickViewModal").then(mod => mod.QuickViewModal), {
    ssr: false
});

import { Product } from "@/types/product.types";
import { useProducts, useSpecialOffers, useNewArrivals, useBestSellers } from "@/features/products/hook/useProducts";
import { useHeroSlides } from "@/features/settings/hook/useCarousel";

export default function LandingPage() {
    const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);

    const handleQuickView = (product: Product) => {
        setSelectedProduct(product);
        setIsModalOpen(true);
    };

    //api integration
    const { data, isLoading } = useProducts({
        page: 1,
        limit: 10,
    });

    const { data: specialOffersData, isLoading: offersLoading } = useSpecialOffers();
    const { data: newArrivalsData, isLoading: newArrivalsLoading } = useNewArrivals();
    const { data: bestSellersData, isLoading: bestSellersLoading } = useBestSellers();

    const { data: heroSlides, isLoading: heroLoading } = useHeroSlides();

    const mappedSlides = heroSlides?.map((slide) => ({
        title: slide.title,
        subtitle: slide.subTitle,
        imageUrl: slide.imageUrl,
        buttonText: slide.buttonText,
        flexDirection: "end" as const,
    })) || [];

    return (
        <div className="flex flex-col min-h-screen">
            <main>
                {heroLoading ? (
                    <div className="w-full h-[85vh] md:h-175 bg-slate-100 animate-pulse flex items-center justify-center">
                        <div className="flex flex-col items-center gap-4">
                            <div className="h-12 w-64 bg-slate-200 rounded-lg" />
                            <div className="h-4 w-96 bg-slate-200 rounded-lg" />
                        </div>
                    </div>
                ) : (
                    <HeroSection slides={mappedSlides} />
                )}

                <HowItWorks />

                <FeaturedPack />

                <SpecialOffers
                    offer={specialOffersData ?? null}
                    onQuickView={handleQuickView}
                    isLoading={offersLoading}
                />

                <ProductGridSection
                    title="Les meilleures ventes"
                    products={bestSellersData?.products ?? []}
                    onQuickView={handleQuickView}
                    isLoading={bestSellersLoading}
                />

                <ProductGridSection
                    title="Nouveaux arrivages"
                    products={newArrivalsData?.products ?? []}
                    onQuickView={handleQuickView}
                    isLoading={newArrivalsLoading}
                    viewAllHref="/new-arrival"
                />

                <AlgeriaSection />

                <ProductGridSection
                    title="Explorez nos produits"
                    products={data?.products ?? []}
                    onQuickView={handleQuickView}
                    isLoading={isLoading}
                />
            </main>

            {isModalOpen && (
                <QuickViewModal
                    product={selectedProduct}
                    isOpen={isModalOpen}
                    onClose={() => setIsModalOpen(false)}
                />
            )}
        </div>
    );
}

